import { useState, useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { CheckCircle, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useBalanceSheet } from '@/hooks/use-reports';
import type { ReportSection } from '@/types';
import { ExportButton } from '@/components/reports/export-button';
import { formatCurrency } from '@/lib/utils';

export const Route = createFileRoute('/reports/balance-sheet')({
  component: BalanceSheetPage,
});


function ReportLineRow({ 
  line, 
  depth = 0 
}: { 
  line: any; 
  depth?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = line.children && line.children.length > 0;
  
  return (
    <>
      <div
        className={`flex justify-between py-0.5 text-sm ${line.isBold || line.isHeader ? 'font-bold' : ''} ${hasChildren ? 'cursor-pointer hover:bg-muted/50 rounded' : ''}`}
        style={{ paddingLeft: `${depth * 16}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        <span className="flex items-center gap-1">
          {hasChildren ? (
            isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />
          ) : (
            <span className="w-4 shrink-0" />
          )}
          {line.accountCode && !line.isHeader && (
            <span className="font-mono text-xs text-muted-foreground w-16 shrink-0">
              {line.accountCode}
            </span>
          )}
          <span>{line.accountName}</span>
        </span>
        <span className="font-mono text-right">{formatCurrency(line.amount)}</span>
      </div>
      
      {hasChildren && isExpanded && line.children.map((child: any, i: number) => (
        <ReportLineRow key={i} line={child} depth={depth + 1} />
      ))}

      {(line.isHeader || hasChildren) && isExpanded && (
        <div 
          className="flex justify-between py-0.5 text-sm font-bold border-t border-black/10 mt-0.5 mb-1"
          style={{ paddingLeft: `${depth * 16 + 20}px` }}
        >
          <span className="underline decoration-1 underline-offset-2 uppercase">Jumlah {line.accountName}</span>
          <span className="underline decoration-1 underline-offset-2 font-mono">{formatCurrency(line.amount)}</span>
        </div>
      )}
    </>
  );
}

function HierarchicalSectionDisplay({ 
  section,
  isRoot = true,
}: { 
  section: ReportSection;
  isRoot?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className={`space-y-1 ${isRoot ? 'mb-6' : 'mt-4'}`}>
      <h3 
        className={`font-bold text-sm uppercase flex items-center gap-1 cursor-pointer hover:text-primary ${isRoot ? 'border-b-2 border-black pb-1 mb-2' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        {section.title}
      </h3>
      
      {isExpanded && (
        <div className="space-y-0 pl-1">
          {section.lines && section.lines.length > 0 ? (
            section.lines.map((line, i) => (
              <ReportLineRow key={i} line={line} depth={0} />
            ))
          ) : !section.subSections ? (
            <div className="text-sm text-muted-foreground py-1 italic pl-5">Tidak ada data</div>
          ) : null}

          {section.subSections && section.subSections.map((sub, i) => (
            <HierarchicalSectionDisplay key={i} section={sub} isRoot={false} />
          ))}
        </div>
      )}

      {isRoot && (
        <div className="flex justify-between font-bold border-t-2 border-black py-1 mt-2 text-sm bg-muted/5 px-1">
          <span className="uppercase">TOTAL {section.title}</span>
          <span className="font-mono">{formatCurrency(section.total)}</span>
        </div>
      )}
    </div>
  );
}

function BalanceSheetPage() {
  const [asOfDate, setAsOfDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const reportRef = useRef<HTMLDivElement>(null);
  const { data: report, isLoading, error } = useBalanceSheet(asOfDate);

  useEffect(() => {
    if (report && reportRef.current) {
      setTimeout(() => {
        reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [report]);

  return (
    <div className="flex flex-col gap-4 w-full overflow-y-auto pb-10">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight">Neraca (Balance Sheet)</h1>
        <ExportButton 
          reportType="balance-sheet" 
          params={{ asOfDate }}
        />
      </div>

      {/* Date Selection */}
      <Card className="w-fit">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs font-medium">Per Tanggal</Label>
              <Input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                className="w-40"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-destructive">
          <p>Gagal memuat neraca.</p>
        </div>
      ) : report ? (
        <Card ref={reportRef} className="overflow-hidden">
          <CardHeader className="text-center pb-4 border-b">
            <CardTitle className="text-lg font-bold">LAUNDRY POS</CardTitle>
            <div className="text-sm font-medium">NERACA (BALANCE SHEET)</div>
            <div className="text-xs text-muted-foreground">
              Per Tanggal {format(new Date(report.asOfDate), 'dd MMMM yyyy')}
            </div>
            <div className="flex justify-center pt-2">
              {report.isBalanced ? (
                <Badge className="bg-green-600 text-white gap-1 text-xs">
                  <CheckCircle className="h-3 w-3" />
                  SEIMBANG
                </Badge>
              ) : (
                <Badge variant="destructive" className="gap-1 text-xs">
                  <AlertCircle className="h-3 w-3" />
                  TIDAK SEIMBANG
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-6">
            {report.sections && report.sections.length > 0 ? (
              // New Hierarchical Tree Rendering
              <>
                {report.sections.map((section, idx) => (
                  <HierarchicalSectionDisplay key={idx} section={section} />
                ))}
              </>
            ) : (
              // Legacy Flat Rendering Fallback
              <>
                {/* Assets */}
                <HierarchicalSectionDisplay section={report.assets} />

                {/* Liabilities */}
                <HierarchicalSectionDisplay section={report.liabilities} />

                {/* Equity */}
                <HierarchicalSectionDisplay section={report.equity} />
              </>
            )}

            {/* Grand Totals */}
            <div className="border-t-2 border-black pt-4 space-y-2">
              <div className="flex justify-between font-bold text-sm bg-muted/30 px-4 py-2 rounded">
                <span className="uppercase">TOTAL AKTIVA</span>
                <span className="font-mono">{formatCurrency(report.totalAssets)}</span>
              </div>
              <div className="flex justify-between font-bold text-sm bg-muted/30 px-4 py-2 rounded">
                <span className="uppercase">TOTAL KEWAJIBAN & MODAL</span>
                <span className="font-mono">{formatCurrency(report.totalLiabilitiesAndEquity)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
